import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, MessageBarBody, mergeClasses } from '@fluentui/react-components';
import { Add20Regular, ArrowReset20Regular, Checkmark20Regular, Edit20Regular } from '@fluentui/react-icons';
import GridLayout from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import { GuideDrawer } from '../../theme/components/GuideDrawer';
import { GuideEmptyState } from '../../theme/components/GuideEmptyState';
import { GuideMessageBar } from '../../theme/components/GuideMessageBar';
import { useCurrentUserId, useMeasuredWidth } from './Dashboard.Hooks';
import { addWidget, applyLayout, defaultDashboardConfig, removeWidget, toGridLayout, updateWidgetSettings } from './Dashboard.Logic';
import { useDashboardSync } from './Dashboard.Sync';
import { WIDGET_DEFINITIONS, WidgetDefinition, widgetCatalog } from './Dashboard.Registry';
import { DASHBOARD_COLS, DASHBOARD_MARGIN, DASHBOARD_ROW_HEIGHT, DashboardLayoutItem, WidgetInstance, WidgetKind, WidgetSettings } from './Dashboard.Types';
import { WidgetFrame } from './WidgetFrame';
import './Dashboard.Styles.css';

/** Debajo de este ancho los widgets se apilan en una columna y no se pueden mover. */
export const DASHBOARD_NARROW_WIDTH = 720;
/** Ancho que se asume mientras el contenedor todavía no se midió (evita un primer render en cero). */
const UNMEASURED_WIDTH = 1200;

interface DashboardProps {
  /** Título de la sección (p. ej. el saludo). */
  heading?: React.ReactNode;
}

/**
 * Panel principal configurable: grilla de 12 columnas con widgets que el usuario agrega, mueve y
 * redimensiona en modo "Personalizar". La disposición se guarda por usuario.
 */
export const DashboardComponent = ({ heading }: DashboardProps) => {
  const { t } = useTranslation();
  const userId = useCurrentUserId();
  const { config, commit, status } = useDashboardSync(userId);
  const [editing, setEditing] = React.useState(false);
  const [catalogOpen, setCatalogOpen] = React.useState(false);
  const [resetOpen, setResetOpen] = React.useState(false);
  const [settingsFor, setSettingsFor] = React.useState<string | undefined>(undefined);
  const { ref: containerRef, width: measuredWidth } = useMeasuredWidth();
  const width = measuredWidth > 0 ? measuredWidth : UNMEASURED_WIDTH;
  const narrow = measuredWidth > 0 && measuredWidth < DASHBOARD_NARROW_WIDTH;

  // Tipado estructural (no el `Layout` de la librería) para no depender de la versión de sus tipos.
  const onLayoutChange = (layout: ReadonlyArray<DashboardLayoutItem>) => commit((current) => applyLayout(current, layout));
  const onAdd = (kind: WidgetKind) => commit((current) => addWidget(current, kind));
  const onRemove = (id: string) => commit((current) => removeWidget(current, id));
  const onSettingsChange = (id: string, settings: WidgetSettings) => commit((current) => updateWidgetSettings(current, id, settings));
  const onReset = () => {
    commit(defaultDashboardConfig());
    setResetOpen(false);
  };

  const gridLayout = React.useMemo(() => toGridLayout(config), [config]);
  const addedKinds = React.useMemo(() => new Set(config.widgets.map((widget) => widget.kind)), [config.widgets]);

  const renderWidget = (widget: WidgetInstance) => {
    const definition: WidgetDefinition = WIDGET_DEFINITIONS[widget.kind];
    const Component = definition.component;
    const title = t(definition.titleKey);
    return (
      <WidgetFrame
        title={title}
        icon={definition.icon}
        editing={editing && !narrow}
        hasSettings={definition.hasSettings}
        onSettings={() => setSettingsFor(widget.id)}
        onRemove={() => onRemove(widget.id)}
        link={definition.link ? { to: definition.link.to, label: t(definition.link.labelKey) } : undefined}
        testId={`widget-${widget.kind}`}
      >
        <Component
          widget={widget}
          editing={editing}
          settingsOpen={settingsFor === widget.id}
          onCloseSettings={() => setSettingsFor(undefined)}
          onSettingsChange={(settings) => onSettingsChange(widget.id, settings)}
        />
      </WidgetFrame>
    );
  };

  /** Vacío, pila (pantalla angosta) o grilla. */
  const renderBody = () => {
    if (config.widgets.length === 0) {
      return (
        <GuideEmptyState
          title={t('dashboard.empty.title')}
          text={t('dashboard.empty.text')}
          action={
            <Button
              appearance="primary"
              icon={<Add20Regular />}
              onClick={() => {
                setEditing(true);
                setCatalogOpen(true);
              }}
              data-testid="dashboard-empty-add"
            >
              {t('dashboard.empty.action')}
            </Button>
          }
          data-testid="dashboard-empty"
        />
      );
    }
    if (narrow) {
      return (
        <div className="dashboard__stack" data-testid="dashboard-stack">
          {config.widgets.map((widget) => (
            <div key={widget.id} className="dashboard__stack-item">
              {renderWidget(widget)}
            </div>
          ))}
        </div>
      );
    }
    return (
      <GridLayout
        className="dashboard__grid"
        width={width}
        layout={gridLayout}
        cols={DASHBOARD_COLS}
        rowHeight={DASHBOARD_ROW_HEIGHT}
        margin={DASHBOARD_MARGIN}
        containerPadding={[0, 0]}
        compactType="vertical"
        isDraggable={editing}
        isResizable={editing}
        draggableHandle=".dbw__head"
        draggableCancel="button, a, input, [role='button']"
        resizeHandles={['se']}
        onLayoutChange={onLayoutChange}
      >
        {config.widgets.map((widget) => (
          <div key={widget.id} className="dashboard__item" data-testid={`dashboard-item-${widget.id}`}>
            {renderWidget(widget)}
          </div>
        ))}
      </GridLayout>
    );
  };

  return (
    <div className={mergeClasses('dashboard', editing && 'dashboard--editing')} data-testid="dashboard">
      <div className="dashboard__bar">
        <div className="dashboard__heading">
          {heading}
          {status !== 'idle' && (
            <output className={mergeClasses('dashboard__sync', `dashboard__sync--${status}`)} data-testid="dashboard-sync" data-status={status}>
              {t(`dashboard.sync.${status}`)}
            </output>
          )}
        </div>
        <div className="dashboard__actions">
          {editing ? (
            <>
              <Button appearance="primary" icon={<Add20Regular />} onClick={() => setCatalogOpen(true)} data-testid="dashboard-add">
                {t('dashboard.addWidget')}
              </Button>
              <Button appearance="subtle" icon={<ArrowReset20Regular />} onClick={() => setResetOpen(true)} data-testid="dashboard-reset">
                {t('dashboard.reset')}
              </Button>
              <Button appearance="secondary" icon={<Checkmark20Regular />} onClick={() => setEditing(false)} data-testid="dashboard-done">
                {t('dashboard.done')}
              </Button>
            </>
          ) : (
            <Button appearance="secondary" icon={<Edit20Regular />} onClick={() => setEditing(true)} disabled={narrow} data-testid="dashboard-customize">
              {t('dashboard.customize')}
            </Button>
          )}
        </div>
      </div>

      {editing && (
        <GuideMessageBar intent="info" layout="multiline" data-testid="dashboard-edit-hint">
          <MessageBarBody>{narrow ? t('dashboard.narrowHint') : t('dashboard.editHint')}</MessageBarBody>
        </GuideMessageBar>
      )}

      <div ref={containerRef} className="dashboard__grid-container">
        {renderBody()}
      </div>

      <GuideDrawer
        open={catalogOpen}
        onDismiss={() => setCatalogOpen(false)}
        size="medium"
        title={t('dashboard.catalog.title')}
        subtitle={t('dashboard.catalog.description')}
        secondaryAction={{ label: t('dashboard.catalog.close') }}
        testId="dashboard-catalog"
      >
        <ul className="dashboard__catalog" data-testid="dashboard-catalog-list">
          {widgetCatalog().map((definition) => {
            const added = addedKinds.has(definition.kind);
            return (
              <li key={definition.kind} className="dashboard__catalog-item" data-testid={`catalog-${definition.kind}`}>
                <span className="dashboard__catalog-icon" aria-hidden="true">
                  {definition.icon}
                </span>
                <div className="dashboard__catalog-text">
                  <span className="dashboard__catalog-title">{t(definition.titleKey)}</span>
                  <span className="dashboard__catalog-description">{t(definition.descriptionKey)}</span>
                </div>
                <Button
                  appearance={added ? 'subtle' : 'primary'}
                  size="small"
                  icon={added ? <Checkmark20Regular /> : <Add20Regular />}
                  disabled={added}
                  onClick={() => onAdd(definition.kind)}
                  data-testid={`catalog-add-${definition.kind}`}
                >
                  {added ? t('dashboard.catalog.added') : t('dashboard.catalog.add')}
                </Button>
              </li>
            );
          })}
        </ul>
      </GuideDrawer>

      <Dialog open={resetOpen} modalType="alert" onOpenChange={(_event, data) => setResetOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t('dashboard.resetDialog.title')}</DialogTitle>
            <DialogContent>{t('dashboard.resetDialog.text')}</DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setResetOpen(false)} data-testid="dashboard-reset-cancel">
                {t('dashboard.resetDialog.cancel')}
              </Button>
              <Button appearance="primary" onClick={onReset} data-testid="dashboard-reset-confirm">
                {t('dashboard.resetDialog.confirm')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default DashboardComponent;
